import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useVizImage } from '../../documents/SectionVisualizationsDialog';
import type { DocumentSection, DocumentVisualization } from '../../../core/types/data-models';

/** Full-size viewer for a single visualization, with zoom/fit controls and the
 * related section's content alongside — opened by clicking a VisualViewCard. */
export function VisualViewDialog({
  viz,
  docName,
  section,
  onClose,
}: {
  viz: DocumentVisualization | null;
  docName: string | undefined;
  section: DocumentSection | undefined;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (viz) setZoom(1);
  }, [viz]);

  const { data, isLoading } = useVizImage(viz?.id ?? 0, viz != null);
  const src = data?.imageData ? `data:${data.mimeType};base64,${data.imageData}` : undefined;

  const handleZoomIn = () => setZoom((z) => Math.min(+(z + 0.25).toFixed(2), 4));
  const handleZoomOut = () => setZoom((z) => Math.max(+(z - 0.25).toFixed(2), 0.25));
  const handleZoomReset = () => setZoom(1);

  return (
    <Dialog open={viz !== null} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: '80vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        {docName ?? 'Visualization'}
        {section?.title ? ` — ${section.title}` : ''}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
              <Tooltip title="Zoom in">
                <span>
                  <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 4}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Zoom out">
                <span>
                  <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 0.25}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Fit to window">
                <span>
                  <IconButton size="small" onClick={handleZoomReset} disabled={zoom === 1}>
                    <ZoomOutMapIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {Math.round(zoom * 100)}%
              </Typography>
            </Stack>

            <Box
              sx={{
                overflow: 'auto',
                display: 'flex',
                alignItems: zoom <= 1 ? 'center' : 'flex-start',
                justifyContent: zoom <= 1 ? 'center' : 'flex-start',
                minHeight: 420,
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1,
              }}
            >
              {isLoading || !src ? (
                <CircularProgress />
              ) : (
                <Box
                  component="img"
                  src={src}
                  alt={docName ?? 'Visualization'}
                  sx={{
                    display: 'block',
                    width: zoom !== 1 ? `${zoom * 100}%` : 'auto',
                    maxWidth: zoom === 1 ? '100%' : 'none',
                    maxHeight: zoom === 1 ? 520 : 'none',
                    objectFit: 'contain',
                    transition: 'width 0.15s, max-width 0.15s',
                  }}
                />
              )}
            </Box>
          </Box>

          {section?.content && (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Section Content
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1 }} />
              <Box
                sx={{
                  maxHeight: 220,
                  overflowY: 'auto',
                  fontSize: '0.875rem',
                  '& p': { mt: 0, mb: 1 },
                  '& ul, & ol': { pl: 2.5, mb: 1 },
                  '& li': { mb: 0.25 },
                  // wrap wide tables so they scroll instead of overflowing the dialog
                  '& table': {
                    display: 'block',
                    width: 'max-content',
                    maxWidth: '100%',
                    overflowX: 'auto',
                    borderCollapse: 'collapse',
                    my: 1,
                    fontSize: '0.8125rem',
                  },
                  '& th, & td': {
                    border: '1px solid var(--color-line)',
                    px: 1,
                    py: 0.5,
                    textAlign: 'left',
                    verticalAlign: 'top',
                  },
                  '& th': {
                    fontWeight: 700,
                    bgcolor: 'var(--color-surface-2)',
                  },
                  '& tbody tr:nth-of-type(even)': {
                    bgcolor: 'var(--color-surface-2)',
                  },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
